import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "../../styles/Series.css";

const seriesCache = new Map();
const seriesById = new Map();
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export function getSeriesById(seriesId) {
  return seriesById.get(String(seriesId)) || null;
}

function registerSeriesList(list = []) {
  list.forEach((item) => {
    if (item?.id != null) seriesById.set(String(item.id), item);
    if (item?.seriesInstanceUID) {
      seriesById.set(String(item.seriesInstanceUID), item);
    }
  });
}

function isRasterThumbnail(url) {
  if (!url) return false;
  const clean = String(url).split("?")[0].toLowerCase();
  return (
    /\.(png|jpe?g|gif|webp|bmp)$/.test(clean) ||
    clean.includes("/thumbnail") ||
    clean.includes("image/jpeg") ||
    clean.includes("image/png")
  );
}

const Thumbnail = React.memo(function Thumbnail({ imageUrl, seriesId }) {
  const canvasRef = useRef(null);

  const onDragStart = (event) => {
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ seriesId })
    );
    event.dataTransfer.effectAllowed = "copy";
  };

  useEffect(() => {
    if (!canvasRef.current || !imageUrl) return;

    if (isRasterThumbnail(imageUrl)) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const size = 100;
      canvas.width = size;
      canvas.height = size;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
      };
      img.src = imageUrl;
      return undefined;
    }

    let cancelled = false;

    const loadDicomThumb = async () => {
      try {
        const { imageLoader } = await import("@cornerstonejs/core");
        const imageId = imageUrl.startsWith("wadouri:")
          ? imageUrl
          : `wadouri:${imageUrl}`;
        const loadObject = imageLoader.loadAndCacheImage(imageId);
        const image = loadObject?.promise ? await loadObject.promise : await loadObject;
        if (cancelled || !canvasRef.current || !image?.width || !image?.height) {
          return;
        }

        const pixelData = image.getPixelData();
        const size = 100;
        const canvas = canvasRef.current;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const offscreen = document.createElement("canvas");
        offscreen.width = image.width;
        offscreen.height = image.height;
        const offCtx = offscreen.getContext("2d");
        const imgData = offCtx.createImageData(image.width, image.height);

        if (image.color) {
          const channels = pixelData.length / (image.width * image.height);
          for (let i = 0, p = 0; i < pixelData.length; i += channels, p += 4) {
            imgData.data[p] = pixelData[i];
            imgData.data[p + 1] = pixelData[i + 1];
            imgData.data[p + 2] = pixelData[i + 2];
            imgData.data[p + 3] = 255;
          }
        } else {
          const wc = Array.isArray(image.windowCenter)
            ? image.windowCenter[0]
            : image.windowCenter ?? 40;
          const ww = Array.isArray(image.windowWidth)
            ? image.windowWidth[0]
            : image.windowWidth ?? 400;
          const lower = wc - ww / 2;
          const upper = wc + ww / 2;
          for (let i = 0; i < pixelData.length; i++) {
            let val = ((pixelData[i] - lower) / (upper - lower)) * 255;
            val = Math.min(255, Math.max(0, val));
            const p = i * 4;
            imgData.data[p] = val;
            imgData.data[p + 1] = val;
            imgData.data[p + 2] = val;
            imgData.data[p + 3] = 255;
          }
        }

        offCtx.putImageData(imgData, 0, 0);
        ctx.drawImage(offscreen, 0, 0, image.width, image.height, 0, 0, size, size);
      } catch (error) {
        console.error("[Thumb] FAILED:", imageUrl, error?.message || error);
      }
    };

    loadDicomThumb();
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return (
    <canvas
      ref={canvasRef}
      draggable
      onDragStart={onDragStart}
      style={{ width: "100px", height: "100px", backgroundColor: "#000" }}
    />
  );
});

export default function Series({ onSelectSeries, onSeriesLoad }) {
  const [series, setSeries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const location = useLocation();
  const selectedPatient = location.state?.compareData || {};
  const studyId = selectedPatient?.id;
  const selectRef = useRef(onSelectSeries);
  const loadRef = useRef(onSeriesLoad);

  selectRef.current = onSelectSeries;
  loadRef.current = onSeriesLoad;

  useEffect(() => {
    if (!studyId) return undefined;

    const controller = new AbortController();

    async function loadSeries() {
      try {
        if (seriesCache.has(studyId)) {
          const cached = seriesCache.get(studyId);
          registerSeriesList(cached);
          setSeries(cached);
          loadRef.current?.(cached);
          if (cached.length) {
            setSelectedIndex(0);
            selectRef.current?.(cached[0], 0);
          }
          return;
        }

        const seriesRes = await fetch(`${BASE_URL}/api/studies/${studyId}/series/`, {
          signal: controller.signal,
        });
        if (!seriesRes.ok) throw new Error("Failed to fetch series");

        const seriesData = await seriesRes.json();
        if (!Array.isArray(seriesData)) return;

        const enriched = await Promise.all(
          seriesData.map(async (seq) => {
            const imageRes = await fetch(
              `${BASE_URL}/api/viewer/series/${seq.id}/images/`,
              { signal: controller.signal }
            );
            if (!imageRes.ok) {
              throw new Error(`Failed to load images for series ${seq.id}`);
            }
            const imageData = await imageRes.json();
            return {
              ...seq,
              imageIds: imageData.imageIds
                ? imageData.imageIds.map((item) => item.url)
                : imageData.map((item) => item.url),
            };
          })
        );

        seriesCache.set(studyId, enriched);
        registerSeriesList(enriched);
        setSeries(enriched);
        loadRef.current?.(enriched);
        console.log(enriched,'series')
        if (enriched.length) {
          setSelectedIndex(0);
          selectRef.current?.(enriched[0], 0);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error loading series:", error);
        }
      }
    }

    loadSeries();
    return () => controller.abort();
  }, [studyId]);

  const handleSeriesClick = useCallback(
    (seq, index) => {
      console.log(seq,'seq')
      if (index === selectedIndex) return;
      setSelectedIndex(index);
      onSelectSeries?.(seq, index);
    },
    [onSelectSeries, selectedIndex]
  );

  return (
    <div className="series">
      {series.filter((seq) => seq.modality !== "SR").
        map((seq, index) => (
        <div
          key={seq.id}
          className={`sequence ${selectedIndex === index ? "sequenceActive" : ""}`}
          style={{ cursor: "pointer" }}
        >
          <div onClick={() => handleSeriesClick(seq, index)}>
            <Thumbnail imageUrl={seq.thumbnail} seriesId={seq.id} />
          </div>
          <div className="imageCount">
            {index + 1}/{seq.totalImages}
          </div>
          <div className="sequenceTitle">{seq.seriesName}</div>
        </div>
      ))}
    </div>
  );
}
